import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import find from 'lodash/fp/find';

import IframeResizer from '@rio-cloud/iframe-resizer';
import {
    AI_ASSISTANT_FILES_PATH,
    ARTIFACTS_MANAGEMENT_PATH,
    CAMPAIGN_PATH,
    DELIVERABLES_MANAGEMENT_PATH,
    DEVICES_PATH,
    GROUPS_PATH,
    OVERVIEW_PATH,
    REPORTS_BILLING_PATH,
    VEHICLES_PATH,
} from '~/features/base/constants/routes';
import SidebarRoutes from '~/features/base/components/SidebarRoutes';
import { accessToken } from '~/features/tokenHandling/accessToken';

import DeviceDetailsSidebarRoutes from '~/features/devices/components/DeviceDetailsSidebarRoutes';
import { copyToClipboard } from '~/features/apps/utils/appsUtils';
import PermissionsOverviewDialog from '~/features/base/components/dialogs/PermissionsOverviewDialog';
import { connect } from 'react-redux';
import { showDeliverablePermissionsDialog } from '~/features/artifacts/actions/deliverableActions';
import noop from 'lodash/fp/noop';
import { env } from '~/env';
import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import VehicleSideBarContainer from '~/features/vehicles/components/VehicleSideBarContainer';

const userMenuEnvironment = env.isProdBuild ? 'production' : import.meta.env.MODE;

const { reportsBilling, showArtifacts, showVehicles, campaignManagement, aiAssistantFiles } = env.featureToggles;

const navItems = [{
    key: 'overview',
    route: (
        <NavLink to={`/${OVERVIEW_PATH}`}>
            <FormattedMessage id='intl-msg:subModuleName.overview'/>
        </NavLink>
    ),
}, {
    key: 'devices',
    route: (
        <NavLink to={`/${DEVICES_PATH}`}>
            <FormattedMessage id='intl-msg:subModuleName.devices'/>
        </NavLink>
    ),
}, {
    key: 'groups',
    route: (
        <NavLink to={`/${GROUPS_PATH}`}>
            <FormattedMessage id='intl-msg:subModuleName.groups'/>
        </NavLink>
    ),
}];

const appTitle = <FormattedMessage id='intl-msg:app.title'/>;

/**
 * Container for a device management submodule
 */
export class ApplicationFrame extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            showPermissionsOverview: false,
        };
    }

    containsNavItem = (itemName) => {
        return find(navItem => {
            return navItem.key === itemName;
        }, navItems) !== undefined;
    };

    togglePermissionsOverDialog = () => {
        this.setState({
            ...this.state,
            showPermissionsOverview: !this.state.showPermissionsOverview,
        });
    };

    renderPermissionsOverDialog = () => {
        return (
            <PermissionsOverviewDialog togglePermissionsOverDialog={this.togglePermissionsOverDialog}/>
        );
    };

    render() {
        const {
            homeRoute,
            appMenuUri,
            userLocale,
            showSessionExpired,
            children,
            onHideSessionDialog,
        } = this.props;

        if (!this.containsNavItem('deliverables')) {

            showArtifacts && navItems.push({
                key: 'artifacts',
                route: (
                    <NavLink to={`/${ARTIFACTS_MANAGEMENT_PATH}`}>
                        <FormattedMessage id='intl-msg:subModuleName.deliverables'/>
                    </NavLink>
                ),
            });

            navItems.push({
                key: 'deliverables',
                route: (
                    <NavLink to={`/${DELIVERABLES_MANAGEMENT_PATH}/`}>
                        <FormattedMessage id='intl-msg:deliverables'/>
                    </NavLink>
                ),
            });

            reportsBilling && navItems.push({
                key: 'reportsbilling',
                route: (
                    <NavLink to={`/${REPORTS_BILLING_PATH}`}>
                        <FormattedMessage id='intl-msg:reportsBilling'/>
                    </NavLink>
                ),
            });

            showVehicles && navItems.push({
                key: 'vehicles',
                route: (
                    <NavLink to={`/${VEHICLES_PATH}`}>
                        <FormattedMessage id='intl-msg:subModuleName.vehicles'/>
                    </NavLink>
                ),
            });
            campaignManagement && navItems.push({
                key: 'campaignManagement',
                route: (
                    <NavLink to={`/${CAMPAIGN_PATH}`}>
                        <FormattedMessage id='Campaigns'/>
                    </NavLink>
                ),
            });
            aiAssistantFiles && navItems.push({
                key: 'aiAssistantFiles',
                route: (
                    <NavLink to={`/${AI_ASSISTANT_FILES_PATH}`}>
                        <FormattedMessage id='intl-msg:subModuleName.aiAssistantFiles'/>
                    </NavLink>
                ),
            });
        }

        const appMenu = (
            <IframeResizer
                title='App navigator'
                src={appMenuUri}
                checkOrigin={false}
                style={{ width: '1px', minWidth: '100%', height: '85px', border: '0' }}/>
        );

        const actionBarItems = [
            <ActionBarItem id='userMenu'>
                <ActionBarItem.Icon>
                    <span className='icon uikit-icon uikit-icon icon-cards-list'></span>
                </ActionBarItem.Icon>
                <ActionBarItem.Popover useOffscreen>
                    <h6>Info</h6>
                    {/*<div className='padding-left-15'>*/}
                    {/*    <span className='uikit-icon icon-info-sign text-color-primary'></span>*/}
                    {/*    <span className='text-color-primary'>{'  Build: 23-12-2020:12:04'}</span>*/}
                    {/*</div>*/}
                    <div>
                        <a href='https://confluence.collaboration-man.com/display/RIOCLAID/MDM+OTA+Documentation'
                           target='_blank'>
                            <button type='button' className='btn btn-link'>
                                <span className='uikit-icon icon-detail-view-info' aria-hidden='true'></span>
                                <span className='text-color-primary' aria-hidden='true'>
                                    <FormattedMessage id='intl-msg:otaDocumentation'/>
                                </span>
                            </button>
                        </a>
                    </div>
                    <div>
                        <a href='https://confluence.collaborationlayer-traton.com/display/MDM/MDM+Knowledge+Base'
                           target='_blank'>
                            <button type='button' className='btn btn-link'>
                                <span className='uikit-icon icon-detail-view-info' aria-hidden='true'></span>
                                <span className='text-color-primary' aria-hidden='true'>
                                    <FormattedMessage id='intl-msg:otaKnowledgeBase'/>
                                </span>
                            </button>
                        </a>
                    </div>
                    <h6>User</h6>
                    <div>
                        <button type='button' className='btn btn-link'
                                onClick={() => this.togglePermissionsOverDialog()}>
                            <span className='uikit-icon icon-user-sign' aria-hidden='true'></span>
                            <span className='text-color-primary' aria-hidden='true'>
                                <FormattedMessage id='intl-msg:permissionsApplication'/>
                            </span>
                        </button>
                        <button type='button' className='btn btn-link'
                                onClick={this.props.onDeliverablePermissionsDialog}>
                            <span className='uikit-icon icon-user-sign' aria-hidden='true'></span>
                            <span className='text-color-primary' aria-hidden='true'>
                                <FormattedMessage id='intl-msg:managePermissionsDeliverable'/>
                            </span>
                        </button>
                        <button type='button' className='btn btn-link'
                                onClick={() => copyToClipboard(`Bearer ${accessToken.getAccessToken()}`, false)}>
                            <span className='uikit-icon icon-duplicate' aria-hidden='true'></span>
                            <span className='text-color-primary' aria-hidden='true'>
                                <FormattedMessage id='intl-msg:copyAccessToken'/>
                            </span>
                        </button>
                    </div>
                </ActionBarItem.Popover>
            </ActionBarItem>,
            <DefaultUserMenu
                key='defaultUserMenu'
                environment={userMenuEnvironment}
            />,
        ];

        return (
            <ApplicationLayout>
                {this.state.showPermissionsOverview && this.renderPermissionsOverDialog()}
                <ApplicationLayout.Header>
                    <ApplicationHeader
                        homeRoute={<a href={homeRoute}/>}
                        label={appTitle}
                        appNavigator={appMenu}
                        navItems={navItems}
                        actionBarItems={actionBarItems}/>
                </ApplicationLayout.Header>
                <ApplicationLayout.Sidebar className='bg-lighter'>
                    <SidebarRoutes/>
                </ApplicationLayout.Sidebar>
                <ApplicationLayout.Sidebar className='right'>
                    <DeviceDetailsSidebarRoutes/>
                    <VehicleSideBarContainer/>
                </ApplicationLayout.Sidebar>
                <ApplicationLayout.Body className='responsive'>
                    <NotificationsContainer/>
                    <SessionExpiredDialog
                        locale={userLocale}
                        onClose={onHideSessionDialog}
                        show={showSessionExpired}/>
                    {children}
                </ApplicationLayout.Body>
            </ApplicationLayout>
        );
    }
}

export const mapStateToProps = (state) => ({});

export const mapDispatchToProps = (dispatch) => {
    return {
        onDeliverablePermissionsDialog: () => {
            dispatch(showDeliverablePermissionsDialog());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationFrame);

ApplicationFrame.propTypes = {
    // props
    accessToken: PropTypes.string,
    homeRoute: PropTypes.string,
    appMenuUri: PropTypes.string,
    userLocale: PropTypes.string,
    showSessionExpired: PropTypes.bool,
    // functions
    onHideSessionDialog: PropTypes.func,
    onDeliverablePermissionsDialog: noop,
};
